import React, { useEffect, useMemo, useState } from 'react';
import './Home.css';
import api from '../api/axiosConfig';
import {
  Calendar,
  Bell,
  AlertCircle,
  Wallet,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';

function Home() {
  const [totalData, setTotalData]   = useState({});
  const [remainData, setRemainData] = useState([]);
  const [remainData2, setRemainData2] = useState([]);

  // 1. 누적금액 조회
  useEffect(() => {
    api.get('/api/Home/searchList')
      .then((res) => setTotalData(res.data?.[0] ?? {}))
      .catch((err) => console.error('누적금액 조회 오류:', err));
  }, []);

  // 2. 미결금 조회
  useEffect(() => {
    api.get('/api/Home/remainList')
      .then((res) => setRemainData(res.data || []))
      .catch((err) => console.error('미수금 조회 오류:', err));
  }, []);

  // 3. 잔금 조회
  useEffect(() => {
    api.get('/api/Home/remainList2')
      .then((res) => setRemainData2(res.data || []))
      .catch((err) => console.error('잔금 조회 오류:', err));
  }, []);

  // ── 날짜 유틸 ──────────────────────────────────────────────

  const getDayName = () => {
    const days = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
    return days[new Date().getDay()];
  };

  const getQuarterLabel = () => {
    const m = new Date().getMonth();
    return `Q${m < 3 ? 1 : m < 6 ? 2 : m < 9 ? 3 : 4}`;
  };

  const getRemainingBusinessDays = () => {
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    let count = 0;
    for (let d = new Date(today); d <= lastDay; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) count++;
    }
    return count;
  };

  const getDayOfYear = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), 0, 0);
    return Math.floor((today - start) / (1000 * 60 * 60 * 24));
  };

  const getProgressRates = () => {
    const today = new Date();
    const year  = today.getFullYear();

    const monthStart = new Date(year, today.getMonth(), 1);
    const monthEnd   = new Date(year, today.getMonth() + 1, 0);
    const monthProgress = Math.round(((today - monthStart) / (monthEnd - monthStart)) * 100);

    const yearStart = new Date(year, 0, 1);
    const yearEnd   = new Date(year, 11, 31);
    const yearProgress = Math.round(((today - yearStart) / (yearEnd - yearStart)) * 100);

    return { monthProgress, yearProgress };
  };

  // ── 포맷 유틸 ──────────────────────────────────────────────

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    const n = parseFloat(num.toString().replace(/,/g, ''));
    return isNaN(n) ? '0' : n.toLocaleString('ko-KR', { maximumFractionDigits: 0 });
  };

  const isNewNotice = (dateString) => {
    const diff = new Date().getTime() - new Date(dateString).getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24)) <= 7;
  };

  // ── 날짜 계산 ──────────────────────────────────────────────

  const today = new Date();

  const dayName              = useMemo(() => getDayName(), []);
  const quarterLabel         = useMemo(() => getQuarterLabel(), []);
  const dayOfYear            = useMemo(() => getDayOfYear(), []);
  const remainingBusinessDays = useMemo(() => getRemainingBusinessDays(), []);
  const { monthProgress, yearProgress } = useMemo(() => getProgressRates(), []);

  const daysLeft = useMemo(() => {
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return lastDay.getDate() - today.getDate();
  }, []);

  const todayFormatted = today.toISOString().split('T')[0];

  // ── KPI ───────────────────────────────────────────────────

  const outData = totalData.OUT_DATA || 0;
  const inData  = totalData.IN_DATA  || 0;
  const profit  = outData - inData;
  const profitRate = outData > 0 ? Math.round((profit / outData) * 100) : 0;

  // ── 공지사항 데이터 ────────────────────────────────────────

  const noticeData = [
    {
        date: '2026-05-30', version: 'v1.1.8', title: '매출정산서 업데이트',
        details: ['매출정산서 페이지에 비고 항목이 추가되었습니다.'],
    },
    {
      date: '2026-05-13', version: 'v1.1.7', title: '수금현황 페이지 추가',
      details: ['수금현황 페이지가 추가되었습니다.'],
    },
    {
      date: '2026-03-23', version: 'v1.1.6', title: '기능 개선',
      details: ['매입매출 등록 화면에 등록일 조회 기능이 추가되었습니다.'],
    },
    {
      date: '2026-03-18', version: 'v1.1.5', title: '기능 개선',
      details: [
        '총괄정산서 페이지가 추가되었습니다.',
        '현장등록 페이지에 현장대리인, 공사시작일, 공사종료일 컬럼이 추가되었습니다.',
      ],
    },
    {
      date: '2026-03-12', version: 'v1.1.4', title: '기능 개선 및 오류 수정',
      details: [
        '메모 기능이 추가되었습니다.',
        '그리드 테마가 엑셀 느낌의 테마로 변경되었습니다.',
        '기간별 조회 화면에서 출력 시 자재대/장비대 컬럼은 숨기고 출력됩니다.',
        '거래처 등록 시 거래처명이 기본 예금주값으로 입력됩니다.',
        '매출매입 등록 시 결제일자를 선택하면 결제금액이 자동입력 됩니다.',
      ],
    },
    {
      date: '2026-02-22', version: 'v1.1.3', title: '기능 개선 및 오류 수정',
      details: [
        '매입매출 등록 화면에 거래처 등록 기능이 추가되었습니다.',
        '이제 기타경비의 동일 항목은 금액이 합산되어 표시됩니다.',
        '매출정산서 페이지가 추가되었습니다.',
      ],
    },
    {
      date: '2026-02-08', version: 'v1.1.2', title: '기능 개선 및 오류 수정',
      details: [
        '거래처등록 화면에서 Tab키로 이동 시 입력한 데이터가 사라지는 문제가 수정되었습니다.',
        '매입매출 등록 화면에 열 고정 기능이 추가되었습니다.',
        '비고 칸의 데이터가 일정 길이 이상인 경우 자동 줄바꿈이 추가되었습니다.',
      ],
    },
    {
      date: '2026-02-04', version: 'v1.1.1', title: '기능 개선',
      details: [
        '거래처등록 화면에서 매입매출, 구분, 기타경비 여부를 추가할 수 있습니다.',
        '매출/매입 등록 화면에서 거래처 선택 시 위의 내용이 자동 적용됩니다.',
        '현장 등록, 거래처 등록 화면의 정렬이 가나다순 → 최신등록순으로 변경되었습니다.',
      ],
    },
    {
      date: '2026-01-31', version: 'v1.1.0', title: '기능 개선',
      details: [
        '매입매출 화면의 상단스크롤이 추가되었습니다.',
        '그리드의 너비 및 높이가 개선되었습니다.',
      ],
    },
    {
      date: '2026-01-29', version: 'v1.0.9', title: '오류 수정 및 기능 개선',
      details: [
        '현장별 매출매입 조회 오류가 수정되었습니다.',
        '현장등록, 거래처등록 화면의 한글 입력 오류가 수정되었습니다.',
      ],
    },
    {
      date: '2026-01-25', version: 'v1.0.8', title: '저장 오류 수정',
      details: ['저장 오류가 수정되었습니다.', '이제 HTTPS 접속이 가능합니다.'],
    },
    {
      date: '2025-12-07', version: 'v1.0.7', title: '조회기능 업데이트',
      details: ['등록 화면에서 거래처별 조회가 가능합니다.'],
    },
    {
      date: '2025-12-03', version: 'v1.0.6', title: '홈 화면 변경 및 UI 개선',
      details: [
        '홈화면에서 미결금, 잔금 데이터를 조회할 수 있습니다.',
        '상단에서 로그인 사용자명을 확인할 수 있습니다.',
      ],
    },
    {
      date: '2025-12-02', version: 'v1.0.5', title: '현장별 매출매입 페이지 추가 및 UI 개선',
      details: [
        '현장별 상세 매출/매입을 조회할 수 있는 페이지가 신설되었습니다.',
        '현장등록 및 거래처등록 페이지의 레이아웃이 변경되었습니다.',
      ],
    },
    {
      date: '2025-11-09', version: 'v1.0.4', title: '로그인 프로세스 안정화',
      details: ['특정 브라우저에서 로그인이 튕기는 오류를 수정했습니다.'],
    },
    {
      date: '2025-11-02', version: 'v1.0.3', title: '입력 편의성 기능 추가',
      details: [
        '기타경비(Y/N) 옵션이 추가되었습니다.',
        '행 추가 시 날짜가 조회 기간에 맞춰 자동 설정됩니다.',
        '세금계산서 입력 시 현장명 검색 기능이 강화되었습니다.',
      ],
    },
  ];

  // ── 이익 트렌드 아이콘 ────────────────────────────────────

  const ProfitIcon = () => {
    if (profit > 0)  return <TrendingUp  size={13} strokeWidth={2} aria-hidden="true" />;
    if (profit < 0)  return <TrendingDown size={13} strokeWidth={2} aria-hidden="true" />;
    return <Minus size={13} strokeWidth={2} aria-hidden="true" />;
  };

  // ─────────────────────────────────────────────────────────

  return (
    <div className="home-root">

      {/* ── 1. Hero 섹션 ── */}
      <section className="card card-hero">
        <div className="hero-left">
          <div className="hero-icon-box">
            <Calendar size={18} strokeWidth={1.8} color="#ffffff" aria-hidden="true" />
          </div>
          <div>
            <h2 className="hero-date">{todayFormatted} ({dayName})</h2>
            <p className="hero-sub">{quarterLabel} &nbsp;|&nbsp; 올해 {dayOfYear}번째 날</p>
          </div>
        </div>

        <div className="hero-right">
          <div className="progress-header">
            <span className="progress-label">이번달 진행도</span>
            <span className="progress-value">{monthProgress}%</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${monthProgress}%` }} />
          </div>
          <div className="hero-pills">
            <span className="hero-pill">D-{daysLeft}</span>
            <span className="hero-pill">영업일 {remainingBusinessDays}일</span>
          </div>
        </div>
      </section>

      {/* ── 2. KPI 섹션 ── */}
      <div className="kpi-grid">

        <section className="card kpi-card">
          <div className="kpi-top">
            <span className="kpi-dot blue" />
            <span className="kpi-label">누적 매출</span>
            <span className="kpi-year">{today.getFullYear()}년</span>
          </div>
          <strong className="kpi-amount blue">₩ {formatNumber(outData)}</strong>
        </section>

        <section className="card kpi-card">
          <div className="kpi-top">
            <span className="kpi-dot red" />
            <span className="kpi-label">누적 매입</span>
            <span className="kpi-year">{today.getFullYear()}년</span>
          </div>
          <strong className="kpi-amount red">₩ {formatNumber(inData)}</strong>
        </section>

        <section className="card kpi-card">
          <div className="kpi-top">
            <span className={`kpi-dot ${profit >= 0 ? 'green' : 'red'}`} />
            <span className="kpi-label">총 이익</span>
            <span className="kpi-year">{today.getFullYear()}년</span>
          </div>
          <strong className={`kpi-amount ${profit >= 0 ? 'green' : 'red'}`}>
            ₩ {formatNumber(profit)}
          </strong>
          <p className={`kpi-sub ${profit >= 0 ? 'green' : 'red'}`}>
            <ProfitIcon /> 이익률 {profitRate}%
          </p>
        </section>

      </div>

      {/* ── 3. 콘텐츠 섹션 ── */}
      <div className="content-grid">

        {/* 공지사항 */}
        <section className="card notice-card">
          <header className="section-header">
            <h3 className="section-title">
              <Bell size={14} strokeWidth={2} aria-hidden="true" />
              업데이트
            </h3>
          </header>
          <div className="notice-scroll">
            {noticeData.map((notice, idx) => (
              <div className="notice-item" key={idx}>
                <div className="notice-meta">
                  <span className="v-badge">{notice.version}</span>
                  {isNewNotice(notice.date) && (
                    <span className="new-badge">NEW</span>
                  )}
                  <span className="notice-date">{notice.date}</span>
                </div>
                <p className="notice-title">{notice.title}</p>
                <ul className="notice-details">
                  {notice.details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* 미결금 */}
        <section className="card remain-card">
          <header className="section-header">
            <h3 className="section-title red">
              <AlertCircle size={14} strokeWidth={2} aria-hidden="true" />
              미결금
            </h3>
            <span className="count-badge red">{remainData.length}건</span>
          </header>
          <div className="remain-scroll">
            {remainData.length === 0 ? (
              <p className="empty-msg">미결금 내역이 없습니다</p>
            ) : (
              remainData.map((item) => (
                <div key={item.COST_ID} className="remain-item">
                  <div className="remain-info">
                    <span className="remain-name">{item.CUSTOMER_NAME}</span>
                    <small className="remain-sub">{item.FIELD_NAME} / {item.ISSUED_DATE}</small>
                  </div>
                  <strong className="remain-amount red">₩ {formatNumber(item.REMAIN_AMOUNT)}</strong>
                </div>
              ))
            )}
          </div>
        </section>

        {/* 잔금 */}
        <section className="card remain-card">
          <header className="section-header">
            <h3 className="section-title blue">
              <Wallet size={14} strokeWidth={2} aria-hidden="true" />
              잔금
            </h3>
            <span className="count-badge blue">{remainData2.length}건</span>
          </header>
          <div className="remain-scroll">
            {remainData2.length === 0 ? (
              <p className="empty-msg">잔금 내역이 없습니다</p>
            ) : (
              remainData2.map((item) => (
                <div key={item.COST_ID} className="remain-item">
                  <div className="remain-info">
                    <span className="remain-name">{item.CUSTOMER_NAME}</span>
                    <small className="remain-sub">{item.FIELD_NAME} / {item.ISSUED_DATE}</small>
                  </div>
                  <strong className="remain-amount blue">₩ {formatNumber(item.REMAIN_AMOUNT)}</strong>
                </div>
              ))
            )}
          </div>
        </section>

      </div>

      {/* ── 푸터 ── */}
      <footer className="home-footer">
        <span className="footer-badge">v1.1.8</span>
        <span className="footer-sep">•</span>
        최종 업데이트: 2026-05-30
      </footer>

    </div>
  );
}

export default Home;